import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';

import { IMembershipList, MembershipListType } from 'libs/domain';
import { MingaPermission } from 'libs/domain';
import { mingaSettingTypes } from 'libs/util';
import { Observable, ReplaySubject, filter, from, map, of, takeUntil } from 'rxjs';

import { PermissionsService } from '@app/src/app/permissions';
import { ListMembershipService } from '@app/src/app/services/ListMembership';
import { FormSelectOption } from '@app/src/app/shared/components/form';
import { MingaSettingsService } from '@app/src/app/store/Minga/services';

import { LayoutService } from '@modules/layout/services';

import { KioskPermissionsService } from '@shared/services/kiosk/kiosk-permissions.service';
import { KioskService } from '@shared/services/kiosk/kiosk.service';

import { BehaviorManagerService } from '../../../behavior-manager';
import { FlexTimeManagerMessages } from '../../constants';
import { FtmSettingsMessages } from './constants';

@Component({
  selector: 'mg-ftm-settings',
  templateUrl: './ftm-settings.component.html',
  styleUrls: ['./ftm-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FtmSettingsComponent implements OnInit, OnDestroy {
  /** Constants */
  public readonly FTM_MESSAGES = FlexTimeManagerMessages;
  public readonly MESSAGES = FtmSettingsMessages;
  public readonly SETTINGS = mingaSettingTypes;
  public readonly LIST_TYPES = [MembershipListType.FLEX_MANAGER, MembershipListType.FLEX_REPORT_VIEWER];
  public readonly autoTardyIsEnabled = new FormControl<boolean>(true);

  /** General Observables */
  private readonly _destroyed = new ReplaySubject<void>(1);

  /** Loading */
  public readonly isLoading$: Observable<boolean> = of(false);

  public ftmLists$: Observable<IMembershipList[]>;

  public isPbisEnabled$: Observable<boolean> = this._mingaSettingsService.isPbisModuleEnabled();
  public behaviorSelectOptions$: Observable<FormSelectOption[]> = this._bmService.getBehaviorTypes(false).pipe(
    map(behaviors => {
      return behaviors
        .map(b => ({
          label: b.name,
          contextLabel: b.active ? '' : FtmSettingsMessages.FTM_AUTO_ASSIGN_DISABLED_LABEL,
          value: b.id,
          disabled: !b.active,
        }))
        .sort((a, b) => {
          return +a.disabled - +b.disabled;
        });
    }),
  );
  public currentAutoAssignBehavior$: Observable<number> = this._mingaSettingsService.getSettingValueObs(
    mingaSettingTypes.FTM_AUTO_ASSIGNED_BEHAVIOR,
  );

  public settings$ = this._mingaSettingsService.getAllSettingsAsObject();

  /** Component Constructor */
  constructor(
    public layout: LayoutService,
    private _membershipService: ListMembershipService,
    private _cdr: ChangeDetectorRef,
    private _permissionService: PermissionsService,
    public kioskPermissions: KioskPermissionsService,
    private _kioskService: KioskService,
    private _bmService: BehaviorManagerService,
    private _mingaSettingsService: MingaSettingsService,
  ) {
    this.initFtmSettingsLists();

    this.autoTardyIsEnabled.valueChanges
      .pipe(
        takeUntil(this._destroyed),
        filter(value => !value),
      )
      .subscribe(value => {
        // if auto tardy toggle is disabled we remove the auto assign behavior
        this._mingaSettingsService.updateSetting(mingaSettingTypes.FTM_AUTO_ASSIGNED_BEHAVIOR, 0);
      });
  }

  async ngOnInit() {
    const currentAutoAssignBehavior = await this._mingaSettingsService.getSettingValue(
      mingaSettingTypes.FTM_AUTO_ASSIGNED_BEHAVIOR,
    );
    this.autoTardyIsEnabled.setValue(!!currentAutoAssignBehavior);
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
  }

  initFtmSettingsLists() {
    if (!this._permissionService.hasPermission(MingaPermission.MINGA_PEOPLE_MANAGE)) return;
    const types = [MembershipListType.FLEX_MANAGER, MembershipListType.FLEX_REPORT_VIEWER];
    this.ftmLists$ = from(this._membershipService.getOrCreateMembershipLists(types));
    this._cdr.markForCheck();
  }

  public reloadLists() {
    this.initFtmSettingsLists();
  }

  public async openDisclaimer(value: boolean) {
    if (value) {
      await this._kioskService.openDisclaimer(mingaSettingTypes.FTM_KIOSK);
    }
  }

  public async updateAutoAssignBehavior(behaviorId) {
    this._mingaSettingsService.updateSetting(mingaSettingTypes.FTM_AUTO_ASSIGNED_BEHAVIOR, behaviorId);
  }

  public tardyCheckInChange(isEnabled: boolean) {
    if (!isEnabled) {
      this._mingaSettingsService.updateSetting(mingaSettingTypes.FTM_AUTO_ASSIGNED_BEHAVIOR, 0);
    }
  }
}
